import { useEffect } from "react";
import { GfgButton } from "@/components/ui/GfgButton";
import type { AddressParts } from "@/interfaces/redemption";

interface RedemptionAddressVerifyModalProps {
  /** The address the recipient typed, as display lines. */
  currentLines: string[];
  /** Smarty's proposed/canonical address; omitted when it couldn't be verified. */
  suggestion?: AddressParts;
  /** Short explanation shown under the title. */
  message: string;
  /** Approve the proposed address. */
  onUseSuggested: () => void;
  /** Keep the typed address and continue. */
  onUseAsIs: () => void;
  /** Dismiss and go back to editing the form. */
  onEdit: () => void;
}

const COLUMN =
  "flex flex-1 flex-col items-center rounded-card border border-card-border bg-clear-dust p-4 text-center";
const LABEL = "mb-2 font-lato text-[11px] font-bold uppercase tracking-[1.4px] text-dust-ink";
const ADDR = "mb-4 font-lato text-[14px] leading-[1.5] text-rodeo-black";

/**
 * Address-verification dialog (Smarty). Our Tailwind twin of v1's `#shipping-modal`:
 * shows what the recipient entered next to Smarty's proposal (a "did you mean?"), or
 * — when nothing could be verified — just their address with a gentle nudge to check
 * it. Either way they can proceed with their own address (fail-open), or edit.
 */
export default function RedemptionAddressVerifyModal({
  currentLines,
  suggestion,
  message,
  onUseSuggested,
  onUseAsIs,
  onEdit,
}: RedemptionAddressVerifyModalProps) {
  // Escape dismisses (→ edit), matching the backdrop.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEdit();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onEdit]);

  return (
    // Backdrop is presentational — dismissal is an explicit choice (the buttons) or
    // Escape (above), so there's no backdrop/dialog click handler to keyboard-mirror.
    <div className="fixed inset-0 z-10400 flex items-center justify-center bg-rodeo-black/40 p-4 motion-safe:animate-tail-in">
      <dialog
        open
        aria-modal="true"
        aria-label="Verify address"
        className="static max-h-full w-full max-w-140 overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_20px_60px_rgba(51,40,32,0.25)] sm:p-8"
      >
        <h2 className="mb-2 font-crimson text-[24px] font-normal text-rodeo-black">
          Verify address
        </h2>
        <p className="mb-5 font-lato text-[15px] leading-normal text-graphite">{message}</p>

        <div className="flex flex-col gap-3 sm:flex-row">
          {/* What the recipient entered */}
          <div className={COLUMN}>
            <span className={LABEL}>You entered</span>
            <div className={ADDR}>
              {currentLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
            <GfgButton
              variant={suggestion ? "outline" : "primary"}
              className="mt-auto max-sm:min-h-11"
              aria-label={suggestion ? "Use this address (as entered)" : undefined}
              onClick={onUseAsIs}
            >
              Use this address
            </GfgButton>
          </div>

          {/* Smarty's proposal (when there is one) */}
          {suggestion && (
            <div className={`${COLUMN} border-dark-dust`}>
              <span className={`${LABEL} text-dark-dust`}>We suggest</span>
              <div className={ADDR}>
                <div>{suggestion.street}</div>
                <div>{`${suggestion.city}, ${suggestion.state} ${suggestion.zip}`}</div>
              </div>
              <GfgButton
                variant="primary"
                className="mt-auto max-sm:min-h-11"
                aria-label="Use this address (suggested)"
                onClick={onUseSuggested}
              >
                Use this address
              </GfgButton>
            </div>
          )}
        </div>

        <div className="mt-5 text-center">
          <GfgButton variant="text" className="max-sm:min-h-11" onClick={onEdit}>
            Edit my address
          </GfgButton>
        </div>
      </dialog>
    </div>
  );
}
